/**
 * Service layer per le chiamate API di Bolletto.
 * Contiene solo logica di accesso dati — nessuna trasformazione UI.
 *
 * Tutte le funzioni restituiscono la `data` dalla response axios.
 * Gli errori vengono propagati al chiamante.
 */
import axios from 'axios';
import { API } from '../utils/constants';

const AUTH_OPTS = { withCredentials: true };

// ─── Bills ───────────────────────────────────────────────

/** @returns {Promise<Array>} lista bollette dell'utente corrente */
export const fetchBills = async () => {
  const { data } = await axios.get(`${API}/bills`, AUTH_OPTS);
  return data;
};

/**
 * Carica una bolletta PDF.
 * @param {File} file - file PDF
 * @param {string} leadSource - provenienza lead
 * @returns {Promise<Object>} bolletta creata con extracted_data
 */
export const uploadBill = async (file, leadSource) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('lead_source', leadSource);
  const { data } = await axios.post(`${API}/bills/upload`, formData, {
    ...AUTH_OPTS,
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

/**
 * Elimina una bolletta.
 * @param {string} billId
 */
export const deleteBill = async (billId) => {
  await axios.delete(`${API}/bills/${billId}`, AUTH_OPTS);
};

// ─── Analyses ────────────────────────────────────────────

/** @returns {Promise<Array>} lista analisi salvate dell'utente */
export const fetchAnalyses = async () => {
  const { data } = await axios.get(`${API}/analyses`, AUTH_OPTS);
  return data;
};

/**
 * [ADMIN] Carica TUTTE le analisi di tutti gli account (supervisione, sola lettura).
 * Ogni analisi include owner_name/owner_email/owner_role.
 * @returns {Promise<Array>}
 */
export const fetchAllAnalyses = async () => {
  const { data } = await axios.get(`${API}/admin/all-analyses`, AUTH_OPTS);
  return data;
};

/**
 * Confronta una bolletta con la miglior CTE automatica.
 * Per default è VOLATILE (non salva nel DB). Per finalizzare passare save=true.
 * @param {string} billId
 * @param {string} customerType - 'domestico' | 'condominio' | 'business'
 * @param {number|null} targetSavings - % risparmio target (opzionale)
 * @param {Object} [opts]
 * @param {number} [opts.annualConsumptionOverride] - override consumo annuo
 * @param {boolean} [opts.save=false] - se true salva l'analisi nello storico (finalizza)
 * @returns {Promise<Object>} analisi con competitor_data, energylab_simulation, ecc.
 */
export const compareBill = async (billId, customerType, targetSavings, opts = {}) => {
  const formData = new FormData();
  formData.append('customer_type', customerType);
  if (targetSavings) formData.append('target_savings', parseFloat(targetSavings));
  if (opts.annualConsumptionOverride) formData.append('annual_consumption_override', opts.annualConsumptionOverride);
  if (opts.save) formData.append('save', 'true');
  const { data } = await axios.post(`${API}/bills/${billId}/compare`, formData, AUTH_OPTS);
  return data;
};

/**
 * Confronta una bolletta con una CTE specifica (manuale).
 * Per default VOLATILE; usa save=true per finalizzare.
 * @param {string} billId
 * @param {string} customerType
 * @param {string} cteCode - codice CTE scelto manualmente
 * @param {Object} [opts]
 * @param {number} [opts.annualConsumptionOverride]
 * @param {boolean} [opts.save=false]
 * @returns {Promise<Object>} analisi ricalcolata
 */
export const compareBillManual = async (billId, customerType, cteCode, opts = {}) => {
  const formData = new FormData();
  formData.append('customer_type', customerType);
  formData.append('cte_code', cteCode);
  if (opts.annualConsumptionOverride) formData.append('annual_consumption_override', opts.annualConsumptionOverride);
  if (opts.save) formData.append('save', 'true');
  const { data } = await axios.post(`${API}/bills/${billId}/compare-manual`, formData, AUTH_OPTS);
  return data;
};

/**
 * Elimina in batch più analisi.
 * @param {string[]} ids - array di ID analisi
 * @returns {Promise<{deleted_count: number}>}
 */
export const batchDeleteAnalyses = async (ids) => {
  const { data } = await axios.post(`${API}/analyses/batch-delete`, { ids }, AUTH_OPTS);
  return data;
};

/**
 * Override manuale del consumo annuo su un'analisi esistente.
 * Ricalcola la proiezione annuale con il valore fornito.
 * @param {string} analysisId
 * @param {number} annualConsumption - kWh o smc/anno
 * @returns {Promise<Object>} analisi aggiornata
 */
export const updateAnnualConsumption = async (analysisId, annualConsumption) => {
  const { data } = await axios.post(
    `${API}/analyses/${analysisId}/annual-consumption`,
    { annual_consumption: annualConsumption },
    AUTH_OPTS
  );
  return data;
};

/**
 * Corregge manualmente i dati estratti dall'AI su una bolletta.
 * Utile per fixare al volo gli errori più comuni di Gemini:
 * - client_name (condomini con amministratore, partita IVA al posto del nome utenza, ...)
 * - iva_rate (utenze business con IVA agevolata 10% legittima)
 *
 * Quando cambia iva_rate, iva_amount viene ricalcolato automaticamente sull'imponibile.
 *
 * @param {string} billId
 * @param {{ client_name?: string, iva_rate?: number }} payload
 * @returns {Promise<Object>} bolletta aggiornata
 */
export const editBillExtracted = async (billId, payload) => {
  const { data } = await axios.post(
    `${API}/bills/${billId}/edit-extracted`,
    payload,
    AUTH_OPTS
  );
  return data;
};

// ─── Tariffs ─────────────────────────────────────────────

/**
 * Carica le tariffe CTE disponibili, raggruppate per categoria.
 * @returns {Promise<Object>} es. { "energia_business": [...], "gas_domestico": [...] }
 */
export const fetchTariffs = async () => {
  const { data } = await axios.get(`${API}/tariffs`, AUTH_OPTS);
  return data;
};

// ─── Lead Sources ────────────────────────────────────────

/**
 * Carica le provenienze lead configurate (accessibile a tutti gli autenticati).
 * @returns {Promise<Array<{id, value, label, order}>>}
 */
export const fetchLeadSources = async () => {
  const { data } = await axios.get(`${API}/lead-sources`, AUTH_OPTS);
  return data;
};

// ─── Auth ────────────────────────────────────────────────

/**
 * Cambia la password dell'utente corrente.
 * @param {string} currentPassword
 * @param {string} newPassword
 */
export const changePassword = async (currentPassword, newPassword) => {
  await axios.post(`${API}/auth/change-password`, {
    current_password: currentPassword,
    new_password: newPassword,
  }, AUTH_OPTS);
};
